import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { money, PLACEHOLDER } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { CalendarDays, Clock, Headphones, XCircle } from "lucide-react";

const statusStyle = {
  confirmed: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  pending: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  cancelled: "bg-red-500/15 text-red-400 border-red-500/30",
};

export default function BookingsTab() {
  const { t } = useLang();
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState("active");

  const load = useCallback(async () => {
    const { data } = await api.get("/bookings");
    setBookings(data.bookings);
  }, []);

  useEffect(() => { load(); }, [load]);

  const cancel = async (id) => {
    try {
      await api.post(`/bookings/${id}/cancel`);
      toast.success(t("cancelled"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const active = bookings.filter((b) => b.status !== "cancelled");
  const cancelled = bookings.filter((b) => b.status === "cancelled");
  const list = filter === "active" ? active : cancelled;

  const label = (b) =>
    b.status === "cancelled" ? t("cancelled")
    : b.payment_status !== "paid" ? t("pending_payment")
    : t("confirmed");

  return (
    <div className="ss-rise">
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mb-4">{t("my_bookings")}</h1>

      <div className="flex gap-2 mb-6">
        {[{ id: "active", label: `${t("active")} (${active.length})` }, { id: "cancelled", label: `${t("cancelled")} (${cancelled.length})` }].map((f) => (
          <button
            key={f.id}
            data-testid={`bookings-filter-${f.id}`}
            onClick={() => setFilter(f.id)}
            className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${
              filter === f.id ? "border-primary bg-primary/15 text-foreground" : "border-border bg-card text-muted-foreground"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {list.length === 0 ? (
        <div data-testid="empty-bookings" className="text-center py-20 border border-dashed border-border rounded-2xl">
          <CalendarDays className="w-8 h-8 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">{t("no_bookings")}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {list.map((b) => (
            <div key={b.id} data-testid={`booking-${b.id}`} className="flex gap-4 p-3 rounded-2xl border border-border bg-card">
              <img src={b.studio_photo || PLACEHOLDER} alt="" className="w-24 h-24 rounded-xl object-cover shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-semibold truncate">{b.studio_name}</h3>
                  <Badge variant="outline" className={statusStyle[b.status] || ""}>{label(b)}</Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-1">{b.studio_city}</p>
                <p className="text-sm mt-2 flex items-center gap-3 flex-wrap">
                  <span className="flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" /> {b.date}</span>
                  <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {String(b.start_hour).padStart(2, "0")}:00 · {b.hours}h</span>
                  {b.want_engineer && <span className="flex items-center gap-1 text-emerald-400"><Headphones className="w-3.5 h-3.5" /> {t("engineer_fee")}</span>}
                </p>
                <div className="flex items-center justify-between mt-2">
                  <span className="font-display font-bold text-primary">{money(b.total_price, b.currency)}</span>
                  {b.status !== "cancelled" && (
                    <Button
                      data-testid={`cancel-booking-${b.id}`}
                      onClick={() => cancel(b.id)}
                      variant="ghost"
                      size="sm"
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8"
                    >
                      <XCircle className="w-4 h-4 mr-1" /> {t("cancel_booking")}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
